import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {ICDRLine, ICDRUser} from '../core/_models'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../template/helpers'
import {cdrAPI} from '../../../services/CDRService'
import {appsAPI} from '../../../services/AppsService'
import clsx from 'clsx'
import {CDRSettingsUserItem} from './CDRSettingsUserItem'
import {CDRSettingsLineItem} from './CDRSettingsLineItem'

type Props = {
  show: boolean
  handleClose: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body
const CDRSettingsModal: FC<Props> = ({show, handleClose}) => {
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  // employees (names) tab is hidden when the app does not keep personal data (docs/PERSONAL_DATA.md)
  const personalData = app?.personal_data === true
  const {data: dataUsers, refetch: refreshUsers} = cdrAPI.useGetUsersQuery(appId!)
  const {data: dataLines, refetch: refreshLines} = cdrAPI.useGetLinesQuery(appId!)
  const [users, setUsers] = useState<ICDRUser[]>([])
  const [lines, setLines] = useState<ICDRLine[]>([])

  useEffect(() => {
    if (dataUsers?.users) {
      setUsers(dataUsers?.users)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUsers])

  useEffect(() => {
    if (dataLines) {
      setLines(dataLines.lines)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLines])

  useEffect(() => {
    if (!show) {
      if (dataUsers?.users) {
        setUsers(dataUsers?.users)
      }
      if (dataLines?.lines) {
        setLines(dataLines.lines)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const handleAddNewUser = () => {
    const newUser: ICDRUser = {
      number: '',
      name: '',
    }
    setUsers([...users, newUser])
  }

  const handleAddNewLine = () => {
    const newLine: ICDRLine = {
      number: '',
      name: '',
    }
    setLines([...lines, newLine])
  }

  const handleCancelUser = () => {
    setUsers((prev) => prev.filter((u) => u.id !== undefined))
    refreshUsers()
  }

  const handleCancelLine = () => {
    setLines((prev) => prev.filter((l) => l.id !== undefined))
    refreshLines()
  }

  const handlerClose = () => {
    refreshUsers()
    refreshLines()
    handleClose()
  }

  return createPortal(
    <Modal show={show} onHide={handlerClose} size='lg' centered>
      <Modal.Header closeButton className='p-5'>
        {/* <Modal.Title>{intl.formatMessage({id: 'APPS.DELETE.MODAL.TITTLE'})}</Modal.Title> */}
        <Modal.Title>Настройка журнала звонков</Modal.Title>
      </Modal.Header>
      <div className='d-flex justify-content-center py-2'>
        <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0'>
          {personalData && (
            <li className='nav-item'>
              <a className='nav-link active' data-bs-toggle='tab' href='#cb_tab_pane_contacts'>
                <span className='small text-gray-700'>Сотрудники</span>
              </a>
            </li>
          )}
          <li className='nav-item'>
            <a className={clsx('nav-link', {active: !personalData})} data-bs-toggle='tab' href='#cb_tab_pane_lines'>
              <span className='small text-gray-700'>Линии</span>
            </a>
          </li>
        </ul>
      </div>
      <Modal.Body className='p-5' style={{maxHeight: '70vh', overflowY: 'auto'}}>
        <div className='d-flex flex-column bd-highlight'>
          <div className='tab-content' id='cdr_settings'>
            {personalData && (
              <div className='tab-pane fade show active' id='cb_tab_pane_contacts' role='tabpanel'>
                <table className='table align-middle table-row-dashed fs-7 gy-5 dataTable no-footer'>
                  <thead>
                    <tr className='fs-9 text-start text-center'>
                      <th role='columnheader' className='min-w-150px p-2'>
                        {/* {intl.formatMessage({id: 'APPS.SETTINGS.AMOCRM.LINES.HEADER.LINE'})} */}
                        Внутренний номер
                      </th>
                      <th role='columnheader' className='min-w-200px p-2'>
                        Имя сотрудника
                      </th>
                      <th role='columnheader' className='min-w-200px p-2'>
                        Управление
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 &&
                      users.map((user, i) => (
                        <CDRSettingsUserItem key={i} user={user} handleCancel={handleCancelUser} />
                      ))}
                  </tbody>
                </table>
                <div className='d-flex justify-content-center mt-5'>
                  <button className='btn btn-sm btn-primary w-50' onClick={handleAddNewUser}>
                    <KTSVG path='/media/icons/duotune/arrows/arr009.svg' className='svg-icon-muted svg-icon-1x' />
                    Добавить
                  </button>
                </div>
              </div>
            )}
            <div
              className={clsx('tab-pane fade', {'show active': !personalData})}
              id='cb_tab_pane_lines'
              role='tabpanel'
            >
              <table className='table align-middle table-row-dashed fs-7 gy-5 dataTable no-footer'>
                <thead>
                  <tr className='fs-9 text-start text-center'>
                    <th role='columnheader' className='min-w-150px p-2'>
                      Номер линии
                    </th>
                    <th role='columnheader' className='min-w-200px p-2'>
                      Название линии
                    </th>
                    <th role='columnheader' className='min-w-200px p-2'>
                      Управление
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length > 0 &&
                    lines.map((line, i) => <CDRSettingsLineItem key={i} line={line} handleCancel={handleCancelLine} />)}
                </tbody>
              </table>
              <div className='d-flex justify-content-center mt-5'>
                <button className='btn btn-sm btn-primary w-50' onClick={handleAddNewLine}>
                  <KTSVG path='/media/icons/duotune/arrows/arr009.svg' className='svg-icon-muted svg-icon-1x' />
                  Добавить
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>,
    modalsRoot
  )
}

export {CDRSettingsModal}
